import { Controller } from '@hotwired/stimulus';
import { Collapse } from 'bootstrap';
import Logger from '../lib/logger';
const carried = new Map();
class default_1 extends Controller {
    constructor() {
        super(...arguments);
        this.isSwitchingTab = false;
        this.frame = null;
        this.boundSyncIndexCollapse = (event) => this.syncIndexCollapse(event);
        this.boundRestoreCarried = () => this.restoreCarried();
    }
    connect() {
        Logger.debug('BidsBenchmarkController connected', { controller: this.constructor.name });
        this.updateVisibility();
        // Bootstrap's dotted event names cannot be bound through data-action (Stimulus reads the dots as key filters).
        this.element.addEventListener('shown.bs.collapse', this.boundSyncIndexCollapse);
        this.element.addEventListener('hidden.bs.collapse', this.boundSyncIndexCollapse);
        // A frame that is this element keeps its controller across a reload, so it hears the render; one around
        // it brings a new controller, which picks the state up as it connects.
        this.frame = this.element.closest('turbo-frame');
        this.frame?.addEventListener('turbo:frame-render', this.boundRestoreCarried);
        this.restoreCarried();
    }
    disconnect() {
        Logger.debug('BidsBenchmarkController disconnected', { controller: this.constructor.name });
        this.element.removeEventListener('shown.bs.collapse', this.boundSyncIndexCollapse);
        this.element.removeEventListener('hidden.bs.collapse', this.boundSyncIndexCollapse);
        this.frame?.removeEventListener('turbo:frame-render', this.boundRestoreCarried);
    }
    // The supplier price index's language switch (its links' data-action): keeps where the user is before the
    // link reloads the frame.
    carryAcrossReload() {
        if (!this.frame?.id)
            return;
        carried.set(this.frame.id, {
            serviceType: this.serviceTypeValue,
            mediaType: this.mediaTypeValue,
            indexOpen: this.indexCollapseTargets.some(collapse => collapse.classList.contains('show'))
        });
    }
    // Puts the redrawn frame back where the user left it, once: the service and media, when it still has that
    // panel, and the index's collapse opened again. A controller whose element the reload took out leaves it to
    // the new one.
    restoreCarried() {
        const id = this.frame?.id;
        const state = id ? carried.get(id) : undefined;
        if (!id || !state || !this.element.isConnected)
            return;
        carried.delete(id);
        Logger.debug('Restoring the benchmark after a language switch', { ...state });
        const hasPanel = this.serviceTypePanelTargets.some(panel => panel.dataset.serviceType === state.serviceType && panel.dataset.mediaType === state.mediaType);
        if (hasPanel) {
            this.serviceTypeValue = state.serviceType;
            this.mediaTypeValue = state.mediaType;
            // A frame that kept its controller kept its values too, so no change callback redraws the new panels.
            this.updateVisibility();
        }
        if (state.indexOpen) {
            this.indexCollapseTargets.forEach(collapse => Collapse.getOrCreateInstance(collapse, { toggle: false }).show());
        }
    }
    serviceTypeValueChanged() {
        this.updateVisibility();
    }
    mediaTypeValueChanged() {
        this.updateVisibility();
    }
    switchServiceType(event) {
        const button = event.currentTarget;
        const serviceType = button.dataset.bidsBenchmarkServiceTypeParam;
        if (!serviceType)
            return;
        Logger.debug('Switching service type', { serviceType });
        this.serviceTypeValue = serviceType;
    }
    switchMediaType(event) {
        const button = event.currentTarget;
        const mediaType = button.dataset.bidsBenchmarkMediaTypeParam;
        if (!mediaType)
            return;
        Logger.debug('Switching media type baseline', { mediaType });
        this.mediaTypeValue = mediaType;
    }
    // The supplier price index's "more" collapse (the circuit's history chart, the model, each bid's
    // percentile) opens and closes in every panel at once, the hidden ones too, so switching the media or the
    // service shows it as the user left it instead of closed. A collapse already in that state, or on its way
    // there, is left alone, which also ends the echo of the ones this opens or closes.
    syncIndexCollapse(event) {
        const source = event.target;
        if (!this.indexCollapseTargets.includes(source))
            return;
        const open = event.type === 'shown.bs.collapse';
        this.indexCollapseTargets.forEach(collapse => {
            if (collapse.classList.contains('show') === open || collapse.classList.contains('collapsing'))
                return;
            const instance = Collapse.getOrCreateInstance(collapse, { toggle: false });
            if (open) {
                instance.show();
            }
            else {
                instance.hide();
            }
        });
    }
    updateVisibility() {
        if (!this.hasServiceTypeValue || !this.hasMediaTypeValue)
            return;
        // Update active pill state
        this.serviceTypeButtonTargets.forEach(btn => {
            btn.classList.toggle('active', btn.dataset.bidsBenchmarkServiceTypeParam === this.serviceTypeValue);
        });
        this.mediaTypeButtonTargets.forEach(btn => {
            btn.classList.toggle('active', btn.dataset.bidsBenchmarkMediaTypeParam === this.mediaTypeValue);
        });
        // Toggle visibility of panels based on both types
        this.serviceTypePanelTargets.forEach(panel => {
            const isVisible = panel.dataset.serviceType === this.serviceTypeValue && panel.dataset.mediaType === this.mediaTypeValue;
            panel.classList.toggle('d-none', !isVisible);
        });
        // Toggle visibility of corresponding tab bars in the toolbar
        this.serviceTypeTabBarTargets.forEach(tabBar => {
            const isVisible = tabBar.dataset.serviceType === this.serviceTypeValue && tabBar.dataset.mediaType === this.mediaTypeValue;
            tabBar.classList.toggle('d-none', !isVisible);
        });
    }
    selectPeriod(event) {
        const row = event.currentTarget;
        const period = row.dataset.bidsBenchmarkPeriodParam;
        const tabId = row.dataset.bidsBenchmarkTabParam;
        if (!period || !tabId)
            return;
        Logger.debug('Selected benchmark period', { period, tabId });
        // Highlight the selected overview row within the visible panel
        this.overviewRowTargets.forEach(r => {
            // Only affect rows within the currently visible panel to prevent resetting others unnecessarily
            const panel = r.closest('[data-bids-benchmark-target="serviceTypePanel"]');
            if (panel && !panel.classList.contains('d-none')) {
                r.classList.remove('table-active');
            }
        });
        row.classList.add('table-active');
        // Filter provider rows
        this.providerRowTargets.forEach(providerRow => {
            const panel = providerRow.closest('[data-bids-benchmark-target="serviceTypePanel"]');
            if (panel && !panel.classList.contains('d-none')) {
                if (providerRow.dataset.period === period) {
                    providerRow.classList.remove('d-none');
                }
                else {
                    providerRow.classList.add('d-none');
                }
            }
        });
        // Switch tab
        const tabButton = document.getElementById(tabId);
        if (tabButton) {
            this.isSwitchingTab = true;
            tabButton.click();
            this.isSwitchingTab = false;
        }
    }
    resetToBestPeriod(event) {
        if (this.isSwitchingTab)
            return; // Ignore programmatic clicks from selectPeriod
        const tabButton = event.currentTarget;
        const bestPeriod = tabButton.dataset.bidsBenchmarkBestPeriodParam;
        if (!bestPeriod)
            return;
        Logger.debug('Resetting to best period', { bestPeriod });
        // Highlight the corresponding overview row
        this.overviewRowTargets.forEach(r => {
            const panel = r.closest('[data-bids-benchmark-target="serviceTypePanel"]');
            if (panel && !panel.classList.contains('d-none')) {
                r.classList.remove('table-active');
                if (r.dataset.bidsBenchmarkPeriodParam === bestPeriod) {
                    r.classList.add('table-active');
                }
            }
        });
        // Filter provider rows
        this.providerRowTargets.forEach(providerRow => {
            const panel = providerRow.closest('[data-bids-benchmark-target="serviceTypePanel"]');
            if (panel && !panel.classList.contains('d-none')) {
                if (providerRow.dataset.period === bestPeriod) {
                    providerRow.classList.remove('d-none');
                }
                else {
                    providerRow.classList.add('d-none');
                }
            }
        });
    }
}
default_1.targets = ['providerRow', 'overviewRow', 'serviceTypePanel', 'serviceTypeButton', 'serviceTypeTabBar', 'mediaTypeButton',
    'indexCollapse'];
default_1.values = {
    serviceType: String,
    mediaType: String
};
export default default_1;
